/**
 * 自然饱和度（对比 Photoshop 补全第一档缺口 阶段 1）。
 *
 * 跟 `lut.ts` 的 `adjustHsl` 同一 HSL 分解/重建骨架，但饱和度增量按当前
 * 饱和度反向加权：已经很鲜艳的像素涨得少（避免死区过曝），已经发灰的
 * 像素涨得多。这是基础版本——不做肤色检测（真实 Photoshop 对高饱和红色
 * 系皮肤色再打一次折，避免人脸过艳），在完成报告里说明这个简化。
 */
export interface VibranceParams {
  /** 自然饱和度，−100–100。 */
  readonly amount: number;
}

export const DEFAULT_VIBRANCE_PARAMS: VibranceParams = { amount: 0 };

export function applyVibrance(
  r: number,
  g: number,
  b: number,
  params: VibranceParams,
): [number, number, number] {
  const amt = params.amount / 100;
  if (amt === 0) return [r, g, b];

  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;
  if (d > 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
    else if (max === gn) h = ((bn - rn) / d + 2) / 6;
    else h = ((rn - gn) / d + 4) / 6;
  }

  // 全灰像素（d=0）没有色相可言——上面的 h 只是任意选了 0，绝不能借着"涨自然
  // 饱和度"往灰像素里凭空加色，所以灰像素直接跳过，保持 s=0
  let newS = 0;
  if (d > 0) {
    // 涨（amt>0）：权重是"还没饱和的余量"(1-s)；跌（amt<0）：权重是当前饱和度
    // 本身，已经发灰的像素再跌也跌不出什么，已经鲜艳的跌得更明显——跟"涨"对称
    const delta = amt >= 0 ? amt * (1 - s) : amt * s;
    newS = Math.min(1, Math.max(0, s + delta));
  }

  const q = l < 0.5 ? l * (1 + newS) : l + newS - l * newS;
  const p = 2 * l - q;
  const channel = (t: number): number => {
    const tt = (t + 1) % 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  return [
    Math.round(Math.min(255, Math.max(0, channel(h + 1 / 3) * 255))),
    Math.round(Math.min(255, Math.max(0, channel(h) * 255))),
    Math.round(Math.min(255, Math.max(0, channel(h - 1 / 3) * 255))),
  ];
}
